/**
 * voice-call-helpers.ts — helpers over a browser call object.
 *
 * Every call the dialers hold is Twilio-shaped: the Twilio SDK's Call, the Telnyx adapter's
 * (telnyx-device-adapter.ts) or the demo device's. These keep call sites off that surface:
 * `.parameters.CallSid`, `.on('disconnect', …)`, `.disconnect()`.
 */

/** A call from TwilioDeviceContext. Existing SDK-typed values flow in unchanged. */
export type VoiceCall = {
  parameters?: Record<string, string>;
  on?: (event: string, listener: (...args: any[]) => void) => unknown;
  off?: (event: string, listener: (...args: any[]) => void) => unknown;
  removeListener?: (event: string, listener: (...args: any[]) => void) => unknown;
  disconnect?: () => void;
  [key: string]: any;
};

type CallParamKey = 'To' | 'From' | 'LeadId' | 'CallSid';

/** The server's id for the call (Telnyx: its call_control_id, never the SDK's own call.id). */
export function getCallSid(call: VoiceCall | null | undefined): string | null {
  return call?.parameters?.CallSid ?? null;
}

export function getCallParam(call: VoiceCall | null | undefined, key: CallParamKey): string | undefined {
  return call?.parameters?.[key];
}

/** Subscribe to the call's disconnect. Returns an unsubscribe fn. */
export function onCallDisconnect(call: VoiceCall | null | undefined, cb: () => void): () => void {
  if (!call?.on) return () => {};
  call.on('disconnect', cb);
  return () => {
    try {
      if (typeof call.off === 'function') call.off('disconnect', cb);
      else call.removeListener?.('disconnect', cb);
    } catch { /* ignore */ }
  };
}

export function hangupCall(call: VoiceCall | null | undefined): void {
  try {
    call?.disconnect?.();
  } catch { /* ignore — call may already be torn down */ }
}

/**
 * The agent's hang-up request (`send` POSTs /calls/:sid/cancel), retried through a database
 * blip. That request is what ends the customers' legs, and during a pooler drop it 503'd and was
 * dropped silently: Nikk Gjurashaj's two answered lines stayed up until he ended the session
 * (2026-10-08). Retries only a blip (the interceptor rejects a POST with the server's body, so
 * {error:'ServiceUnavailable'}, or "Network Error" when it never arrived), and only while
 * `stillThisCall()` holds: the request names the session's conference, which a race session
 * reuses, so a late retry after the next call started would end that call.
 */
export async function sendHangupCancel(
  send: () => Promise<unknown>,
  opts: { stillThisCall: () => boolean; onGiveUp: () => void; waitsMs?: number[] },
): Promise<'sent' | 'failed' | 'superseded' | 'gave-up'> {
  const waits = opts.waitsMs ?? [1000, 2000, 4000];
  for (let attempt = 0; ; attempt++) {
    try {
      await send();
      return 'sent';
    } catch (err: any) {
      const blip = err?.error === 'ServiceUnavailable' || /network error/i.test(String(err?.message ?? err ?? ''));
      if (!blip) return 'failed';
      if (attempt >= waits.length) {
        opts.onGiveUp();
        return 'gave-up';
      }
      await new Promise((r) => setTimeout(r, waits[attempt]));
      if (!opts.stillThisCall()) return 'superseded';
    }
  }
}
